import { useCallback, useMemo } from "react";
import type {
  MorphemeGlyphTileGraphemeMorphemeSlice,
  MorphemeGlyphTileSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import {
  type GlyphStyle,
  GraphemeMorphemeGlyphTileUi,
  type MorphemeGlyphTileCompletion,
  type MorphemeGlyphTileStage,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import {
  type AppSelectionItem,
  morphemeSelectionItem,
} from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";

export type GraphemeMorphemeGlyphTileLinkProps = {
  className?: string;
  completion?: MorphemeGlyphTileCompletion;
  glyph: GraphemeGlyph;
  glyphStyle?: GlyphStyle;
  href: string;
  /**
   * Whether this grapheme's card isn't version-complete yet — forwarded to the tile so it can fade
   * under the `fadeMissingMnemonicEnabled` setting. Resolved from a batched per-grid completion read.
   */
  isMissingMnemonic?: boolean;
  /**
   * Pre-resolved pinyin slice from a batched parent query. Passed together with `stage`, the tile
   * fires no per-tile queries (fast path for large grids). See `GraphemeMorphemeGlyphTileUi`.
   */
  morphemeGlyphTileSlice?: MorphemeGlyphTileGraphemeMorphemeSlice;
  /**
   * Navigation override. When provided, the tap calls this with the resolved morpheme instead of
   * pushing via the generic panels navigator — used to route to a destination whose own navigator
   * warms a larger query set (e.g. the Learn review card, which suspends on the fsrs resolution the
   * generic navigator never prefetches). The tile then does NOT mount the generic navigator's
   * mutations at all (a `useMutation` trio per tile is real cost in a grid of hundreds).
   * `href`/`prefixParams`/`view` are ignored when set (the override owns the destination).
   * Selecting-mode still short-circuits the tap before this runs.
   */
  onNavigate?: (morpheme: string) => void;
  prefixParams?: MorphemeHrefObject["params"];
  /** Override the selectable leaves represented by this glyph (the Deck passes exact user cards). */
  selectionItems?: readonly AppSelectionItem[];
  showPrimaryPinyin?: boolean;
  stage?: MorphemeGlyphTileStage;
  uncolored?: boolean;
  /**
   * Which panel to open the target card on. Defaults to `"mnemonics"` — the glyph-grid entry
   * point (Levels/Cards grids open a tapped tile straight to its mnemonic). Callers that open the
   * card to match the panel the user is currently on (e.g. the info-panel "Similar" row when
   * viewed from Browse/Search) pass `"info"`.
   */
  view?: "mnemonics" | "info";
};

type GraphemeMorphemeGlyphTileLinkUiProps = Omit<
  GraphemeMorphemeGlyphTileLinkProps,
  "href" | "onNavigate" | "prefixParams" | "view"
> & {
  onPress: (morpheme: string) => void;
};

/** The selection wiring + tile shared by both navigation variants. */
function GraphemeMorphemeGlyphTileLinkUi({
  className,
  completion,
  glyph,
  glyphStyle,
  isMissingMnemonic,
  morphemeGlyphTileSlice,
  onPress,
  selectionItems,
  showPrimaryPinyin,
  stage,
  uncolored,
}: GraphemeMorphemeGlyphTileLinkUiProps) {
  const defaultSelectionItems = useMemo(
    () => [morphemeSelectionItem(glyph.original)],
    [glyph.original],
  );
  const { consumePress, isSelected, onLongPress } = useAppSelection(
    selectionItems ?? defaultSelectionItems,
  );
  // No post-press shade: the tap's navigation commits on the press, so the next screen (with its
  // own skeleton) is the acknowledgement.
  const handlePress = useCallback(
    (_morphemeGlyphTileSlice: MorphemeGlyphTileSlice) => {
      if (consumePress()) {
        return;
      }
      onPress(glyph.original);
    },
    [consumePress, glyph.original, onPress],
  );

  return (
    <GraphemeMorphemeGlyphTileUi
      className={className}
      completion={completion}
      glyphStyle={glyphStyle}
      grapheme={glyph.original}
      graphemeGlyph={glyph}
      isMissingMnemonic={isMissingMnemonic}
      isSelected={isSelected}
      morphemeGlyphTileSlice={morphemeGlyphTileSlice}
      onLongPress={onLongPress}
      onPress={handlePress}
      showPrimaryPinyin={showPrimaryPinyin}
      stage={stage}
      uncolored={uncolored}
    />
  );
}

function SelfNavigatingGraphemeMorphemeGlyphTileLink({
  href,
  prefixParams,
  view = "mnemonics",
  ...props
}: Omit<GraphemeMorphemeGlyphTileLinkProps, "onNavigate">) {
  const cardPageNavigator = useMorphemePanelsHrefNavigator();
  const pushMutate = cardPageNavigator.push.mutate;
  const handlePress = useCallback(
    (morpheme: string) => {
      pushMutate({
        morpheme,
        prefix: {
          href,
          params: { ...prefixParams, view },
        },
      });
    },
    [href, prefixParams, pushMutate, view],
  );
  return <GraphemeMorphemeGlyphTileLinkUi onPress={handlePress} {...props} />;
}

export function GraphemeMorphemeGlyphTileLink({
  href,
  onNavigate,
  prefixParams,
  view,
  ...props
}: GraphemeMorphemeGlyphTileLinkProps) {
  if (onNavigate !== undefined) {
    return <GraphemeMorphemeGlyphTileLinkUi onPress={onNavigate} {...props} />;
  }
  return (
    <SelfNavigatingGraphemeMorphemeGlyphTileLink
      href={href}
      prefixParams={prefixParams}
      view={view}
      {...props}
    />
  );
}
